import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { ArrowRight, CheckCircle2 } from "lucide-react";
import { API } from "../lib/api";
import { SeoHead } from "../components/common/SeoHead";
import { track } from "../lib/track";

const JAKARTA = { fontFamily: "'Plus Jakarta Sans','Manrope',sans-serif" };

const FIELD_CLS = "w-full rounded-xl border border-[#35205F]/20 bg-white px-4 py-3 text-sm outline-none transition-colors focus:border-[#F47735]";
const LABEL_CLS = "mb-1.5 block text-[11px] font-bold uppercase tracking-[0.15em] text-[#35205F]/70";
const ERR_CLS = "mt-1 text-xs font-semibold text-[#c0392b]";

export default function StudioEnquiryPage() {
  const [params] = useSearchParams();
  const [packages, setPackages] = useState([]);
  const [form, setForm] = useState({
    business_name: "", contact_name: "", reg_no: "", email: "", phone: "",
    industry: "", website: "", services: params.get("package") ? [params.get("package")] : [],
    brief: "", budget: "", timeline: "", preferred_datetime: "", consent: false, website_confirm: "",
  });
  const [errors, setErrors] = useState({});
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(null);

  useEffect(() => {
    fetch(`${API}/studio/packages`).then((r) => r.json()).then((d) => setPackages(d.packages || [])).catch(() => {});
  }, []);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const toggleService = (id) =>
    set("services", form.services.includes(id) ? form.services.filter((s) => s !== id) : [...form.services, id]);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setErrors({});
    try {
      const res = await fetch(`${API}/studio/enquiries`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(form),
      });
      const data = await res.json();
      if (res.status === 422) {
        setErrors(typeof data.detail === "object" ? data.detail : {});
        window.scrollTo({ top: 0, behavior: "smooth" });
        return;
      }
      if (!res.ok) throw new Error(data.detail || "Submission failed");
      track("consulting_lead", { source: "studio_enquiry", page: "/consulting/digital-studio/enquire" });
      setDone(data);
    } catch (err) {
      setErrors({ form: err.message });
    } finally {
      setBusy(false);
    }
  };

  if (done) {
    return (
      <main style={JAKARTA} className="bg-[#F8F5EF] py-32 text-[#241b3a]" data-testid="studio-enquiry-success">
        <SeoHead title="Enquiry received — IAGSB Digital Studio" description="Your Digital Studio enquiry has been received." path="/consulting/digital-studio/enquire" />
        <div className="container-site max-w-xl text-center">
          <CheckCircle2 className="mx-auto h-14 w-14 text-[#17A6A0]" strokeWidth={1.5} />
          <h1 className="mt-6 text-3xl font-extrabold text-[#35205F]">Terima kasih — enquiry received.</h1>
          <p className="mt-4 text-sm leading-relaxed text-[#241b3a]/70">
            Your reference is <strong data-testid="studio-enquiry-ref">{done.reference}</strong>. A real human (not a bot) will review it and reply by email or WhatsApp. Nothing is charged at this stage — every project begins with a written quotation you approve first.
          </p>
        </div>
      </main>
    );
  }

  return (
    <main style={JAKARTA} className="bg-[#F8F5EF] py-28 text-[#241b3a]" data-testid="studio-enquiry-page">
      <SeoHead title="Start a project — IAGSB Digital Studio" description="Tell us about your business. Written quotation before any payment." path="/consulting/digital-studio/enquire" />
      <div className="container-site max-w-2xl">
        <p className="text-[11px] font-bold uppercase tracking-[0.3em] text-[#F47735]">Project enquiry</p>
        <h1 className="mt-4 text-3xl font-extrabold tracking-tight text-[#35205F] sm:text-4xl">Tell us about your business.</h1>
        <p className="mt-3 text-sm leading-relaxed text-[#241b3a]/65">Three minutes, honest answers. We reply personally with next steps — never an automatic charge.</p>

        {errors.form && <p className="mt-6 rounded-xl border border-[#c0392b]/30 bg-[#c0392b]/5 px-4 py-3 text-sm text-[#c0392b]" data-testid="studio-form-error">{errors.form}</p>}

        <form onSubmit={submit} className="mt-10 space-y-6" data-testid="studio-enquiry-form">
          <div className="grid gap-6 sm:grid-cols-2">
            <div>
              <label className={LABEL_CLS} htmlFor="sf-biz">Business / legal name *</label>
              <input id="sf-biz" data-testid="sf-business-name" className={FIELD_CLS} value={form.business_name} onChange={(e) => set("business_name", e.target.value)} />
              {errors.business_name && <p className={ERR_CLS}>{errors.business_name}</p>}
            </div>
            <div>
              <label className={LABEL_CLS} htmlFor="sf-contact">Contact person *</label>
              <input id="sf-contact" data-testid="sf-contact-name" className={FIELD_CLS} value={form.contact_name} onChange={(e) => set("contact_name", e.target.value)} />
              {errors.contact_name && <p className={ERR_CLS}>{errors.contact_name}</p>}
            </div>
            <div>
              <label className={LABEL_CLS} htmlFor="sf-reg">Business registration no. (if any)</label>
              <input id="sf-reg" data-testid="sf-reg-no" className={FIELD_CLS} placeholder="e.g. 202301012345 (…)" value={form.reg_no} onChange={(e) => set("reg_no", e.target.value)} />
            </div>
            <div>
              <label className={LABEL_CLS} htmlFor="sf-industry">Industry</label>
              <input id="sf-industry" data-testid="sf-industry" className={FIELD_CLS} placeholder="e.g. F&B, beauty, retail…" value={form.industry} onChange={(e) => set("industry", e.target.value)} />
            </div>
            <div>
              <label className={LABEL_CLS} htmlFor="sf-email">Email *</label>
              <input id="sf-email" type="email" data-testid="sf-email" className={FIELD_CLS} value={form.email} onChange={(e) => set("email", e.target.value)} />
              {errors.email && <p className={ERR_CLS}>{errors.email}</p>}
            </div>
            <div>
              <label className={LABEL_CLS} htmlFor="sf-phone">WhatsApp number (Malaysia) *</label>
              <input id="sf-phone" data-testid="sf-phone" className={FIELD_CLS} placeholder="01x-xxxxxxx" value={form.phone} onChange={(e) => set("phone", e.target.value)} />
              {errors.phone && <p className={ERR_CLS}>{errors.phone}</p>}
            </div>
            <div className="sm:col-span-2">
              <label className={LABEL_CLS} htmlFor="sf-web">Existing website (if any)</label>
              <input id="sf-web" data-testid="sf-website" className={FIELD_CLS} placeholder="https://…" value={form.website} onChange={(e) => set("website", e.target.value)} />
            </div>
          </div>

          <fieldset>
            <legend className={LABEL_CLS}>Which services do you need?</legend>
            <div className="grid gap-2.5 sm:grid-cols-2">
              {packages.map((p) => (
                <label key={p.id} data-testid={`sf-service-${p.id}`} className={`flex cursor-pointer items-center justify-between gap-3 rounded-xl border px-4 py-3 text-sm transition-colors ${form.services.includes(p.id) ? "border-[#F47735] bg-[#F47735]/10 font-bold text-[#35205F]" : "border-[#35205F]/15 bg-white text-[#241b3a]/75 hover:border-[#35205F]/40"}`}>
                  <span>{p.name} <span className="text-xs font-semibold text-[#17A6A0]">· {p.price}</span></span>
                  <input type="checkbox" className="h-4 w-4 accent-[#F47735]" checked={form.services.includes(p.id)} onChange={() => toggleService(p.id)} />
                </label>
              ))}
            </div>
          </fieldset>

          <div>
            <label className={LABEL_CLS} htmlFor="sf-brief">Project brief *</label>
            <textarea id="sf-brief" rows={4} data-testid="sf-brief" className={FIELD_CLS} placeholder="What does your business do, and what should this project achieve?" value={form.brief} onChange={(e) => set("brief", e.target.value)} />
            {errors.brief && <p className={ERR_CLS}>{errors.brief}</p>}
          </div>

          <div className="grid gap-6 sm:grid-cols-2">
            <div>
              <label className={LABEL_CLS} htmlFor="sf-budget">Budget range</label>
              <select id="sf-budget" data-testid="sf-budget" className={FIELD_CLS} value={form.budget} onChange={(e) => set("budget", e.target.value)}>
                <option value="">Select…</option>
                <option>Under RM2,000</option><option>RM2,000–RM4,000</option><option>RM4,000–RM7,000</option><option>RM7,000+</option><option>Monthly care plan</option>
              </select>
            </div>
            <div>
              <label className={LABEL_CLS} htmlFor="sf-timeline">Timeline</label>
              <select id="sf-timeline" data-testid="sf-timeline" className={FIELD_CLS} value={form.timeline} onChange={(e) => set("timeline", e.target.value)}>
                <option value="">Select…</option>
                <option>Urgent (2–3 weeks)</option><option>1–2 months</option><option>Flexible</option>
              </select>
            </div>
          </div>

          <div>
            <label className={LABEL_CLS} htmlFor="sf-slot">Preferred appointment date &amp; time (optional)</label>
            <input id="sf-slot" type="datetime-local" data-testid="sf-preferred-datetime" className={FIELD_CLS} value={form.preferred_datetime} onChange={(e) => set("preferred_datetime", e.target.value)} />
            <p className="mt-1 text-xs text-[#241b3a]/50">Malaysia time (Asia/Kuala_Lumpur). We confirm by email/WhatsApp — nothing is booked until we approve it.</p>
            {errors.preferred_datetime && <p className={ERR_CLS}>{errors.preferred_datetime}</p>}
          </div>

          {/* honeypot — invisible to humans */}
          <input type="text" name="website_confirm" tabIndex={-1} autoComplete="off" value={form.website_confirm} onChange={(e) => set("website_confirm", e.target.value)} style={{ position: "absolute", left: "-9999px" }} aria-hidden="true" />

          <label className="flex items-start gap-3 text-sm text-[#241b3a]/75" data-testid="sf-consent-wrap">
            <input type="checkbox" className="mt-1 h-4 w-4 accent-[#F47735]" data-testid="sf-consent" checked={form.consent} onChange={(e) => set("consent", e.target.checked)} />
            <span>I consent to IAGSB Digital Studio (Irma Ariandi Global Sdn Bhd) storing and using these details to respond to my enquiry, per the Personal Data Protection Act 2010. *</span>
          </label>
          {errors.consent && <p className={ERR_CLS}>{errors.consent}</p>}

          <button type="submit" disabled={busy} data-testid="sf-submit" className="inline-flex items-center gap-2 rounded-full bg-[#35205F] px-8 py-4 text-sm font-bold text-[#F8F5EF] transition-transform hover:scale-[1.02] disabled:opacity-50">
            {busy ? "Sending…" : "Send enquiry"} <ArrowRight className="h-4 w-4" strokeWidth={2} />
          </button>
        </form>
      </div>
    </main>
  );
}
